import { Injectable } from '@angular/core';
import { Exercise } from '../models/exercise.model';
import { HelperService } from 'src/app/services/helper.service';
import { HttpClient } from '@angular/common/http';
import { AppService } from './app.service';
import { DatabaseService } from './database.service';
import { equalTo, orderByChild } from '@angular/fire/database';
import { StorageService } from './storage.service';
import { LocalAttachment } from './camera.service';

export interface IExerciseFilters {
  order?: "title" | "counter";
  status?: "active" | "archived";
  exerciseType?: string;
  bodyPart?: string;
  equipment?: string;
  searchTerm?: string;
  type?: "system" | "custom" | "recents";
  sortOrder?: "ascending" | "descending",
}

export interface IExerciseFiltersMultiple {
  order?: "title" | "counter";
  status?: "active" | "archived";
  exerciseType?: string[];
  bodyPart?: string[];
  equipment?: string[];
  searchTerm?: string;
  type?: "system" | "custom" | "recents";
  sortOrder?: "ascending" | "descending",
}


@Injectable({
  providedIn: 'root'
})
export class ExerciseService {
  
  constructor(
    private appService: AppService,
    private dbService: DatabaseService,
    private helperService: HelperService,
    private http: HttpClient,
    private storageService: StorageService,
  ) { }

  async getExerciseAsPromise(exerciseId, withPhoto: boolean = false): Promise<Exercise> {
    const snap = await this.dbService.getSnapshot(`exercises/${exerciseId}`)
    if(snap) {
      let exercise = new Exercise(snap);
      exercise.uid = exerciseId;
      exercise.id = exerciseId;
      if (withPhoto && !!exercise.photo) {
        try {
          const image = await this.getExerciseImageAsPromise(exercise.id);
          exercise.img = image;
        } catch (error) {
          console.log("no image");
        }
      }
      return exercise
    } else {
      return null
    }
  }

  getExerciseImageAsPromise(id): Promise<any> {
    return this.storageService.fbGetDownloadURL(`exercises/${id}`);
  }

  updateExercise(exerciseId: string, data: Partial<Exercise>) {
    return this.dbService.updateRef(`exercises/${exerciseId}`, data);
  }

  async updateExerciseCounter(exId: string) {
    const counter = await this.dbService.getSnapshot(`exercises/${exId}/counter`);
    return this.dbService.setRef(`exercises/${exId}/counter`, isNaN(counter)? 1 : counter + 1);
  }

  getRecentlyUsedExercises(userId: string) {
    return this.dbService.getSnapshot(`recentlyUsedExercises/${userId}`);
  }

  async saveRecentlyUsedExercise(userId: string, exerciseId: string) {
    return this.dbService.setRef(`recentlyUsedExercises/${userId}/${exerciseId}`, true);
  }

  async duplicateExerciseToAccount(exerciseId: string, accountId: string, data: any) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/exercises/${exerciseId}/duplicate`, {...data, account: accountId})
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  async getSystemBodyParts() { 
    function findChildren(categories, parent) {
      parent.children = [];
      for (const cat of categories) {
        if(cat.parents && cat.parents[parent.value]) {
          parent.children.push(cat);
        }
        for (const child of parent.children) {
          findChildren(categories, child);
        }
      }
    }

    const snap = await this.dbService.getSnapshot('globalSettings/exercises/bodyPartsV2');
    const detail = snap;
    if(detail) {
      const final = [];
      const toSort = [];
      // Set top level categories
      const categories = this.helperService.transformFirebaseObjectToArray(detail); 
      toSort.push(...categories.filter(cat => !cat.parents));
      for (const cat of toSort) {
        findChildren(categories, cat);
      }
      const noChildren = toSort.filter((item) => {return !item.children || item.children.length === 0});
      const children = toSort.filter((item) => {return item.children?.length > 0});
      final.push(...noChildren, ...children);
      return final;
    } else {
      return null
    }
  }

  async getSystemEquipment() {
    function findChildren(categories, parent) {
      parent.children = [];
      for (const cat of categories) {
        if(cat.parents && cat.parents[parent.value]) {
          parent.children.push(cat);
        }
        for (const child of parent.children) {
          findChildren(categories, child);
        }
      }
    }

    const snap = await this.dbService.getSnapshot('globalSettings/exercises/equipmentV2');
    const detail = snap;
    if(detail) {
      const final = [];
      const toSort = [];
      // Set top level categories
      const categories = this.helperService.transformFirebaseObjectToArray(detail); 
      toSort.push(...categories.filter(cat => !cat.parents));
      for (const cat of toSort) {
        findChildren(categories, cat);
      }
      const noChildren = toSort.filter((item) => {return !item.children || item.children.length === 0});
      const children = toSort.filter((item) => {return item.children?.length > 0});
      final.push(...noChildren, ...children);
      return final;
    } else {
      return null
    }
  }

  async getSystemExerciseTypes() {
    function findChildren(categories, parent) {
      parent.children = [];
      for (const cat of categories) {
        if(cat.parents && cat.parents[parent.value]) {
          parent.children.push(cat);
        }
        for (const child of parent.children) {
          findChildren(categories, child);
        }
      }
    }

    const snap = await this.dbService.getSnapshot('globalSettings/exercises/exerciseTypes');
    const detail = snap;
    if(detail) {
      const final = [];
      const toSort = [];
      // Set top level categories
      const categories = this.helperService.transformFirebaseObjectToArray(detail); 
      toSort.push(...categories.filter(cat => !cat.parents));
      for (const cat of toSort) {
        findChildren(categories, cat);
      }
      const noChildren = toSort.filter((item) => {return !item.children || item.children.length === 0});
      const children = toSort.filter((item) => {return item.children?.length > 0});
      final.push(...noChildren, ...children);
      return final;
    } else {
      return null
    }
  }

  public async getSystemExercises(arr: boolean = false) {
    const snap = await this.dbService.getSnapshot(`exercises`, orderByChild("organization"), equalTo("system"));
    return arr? this.helperService.transformFirebaseObjectToArray(snap) : snap;
  }

  public async getOrganizationExercises(organization, arr: boolean = false) {
    const snap = await this.dbService.getSnapshot(`exercises`, orderByChild("organization"), equalTo(organization));
    return arr ? this.helperService.transformFirebaseObjectToArray(snap) : snap;
  }

  public getOrganizationExercisesList(organization, arr: boolean = false) {
    return this.dbService.getListByRef<Exercise>(`exercises`, "uid", orderByChild("organization"), equalTo(organization));
  }


  createExercise(exerciseObj: any) {
    return this.dbService.pushByRef('exercises', exerciseObj);
  }

  deleteExercise(exerciseId: string) {
    return this.dbService.removeRef(`exercises/${exerciseId}`);
  }

  async addLogoPicture(exerciseId: string, imageData: LocalAttachment): Promise<any> {

    const storagePath = `exercises/${exerciseId}`;
    let attachmentRef: string;

    if(imageData.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, imageData.file);
    }

    if(imageData.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, imageData.nativePath);
    }

    if(!attachmentRef) {
      throw new Error("Failed to upload image");
    }

    await this.dbService.updateRef('/exercises/' + exerciseId, { photo: true });

    return attachmentRef;
  }

  async addExerciseVideo(exerciseId: string, attachment: LocalAttachment): Promise<string> {
    const storagePath = `exercisesV2/${exerciseId}/${attachment.name}`;

    if(attachment.file) {
      return this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      return this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }
  }

  async removeLogoPicture(exerciseId) {
    return this.storageService.fbRemoveFile(`exercises/${exerciseId}`);
  }

  getDefaultEquopmentPreferenceText() {
    return {
      "available": "All equipment available",
      "unavailable": "Equipment unavailable",
      "partial": "Not all equipment may be available"
    };
  }
}
